// /marriage/documents — Wave 69: TH hub of marriage & family document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/marriage/documents";
const URL = `${SITE}${PATH}`;
const EN_URL = `${SITE}/en/marriage/documents`;
const TITLE = "เอกสารสมรสไทย–ต่างชาติ — 6 กลุ่มกรณี | NYC";
const DESC =
  "รวมเอกสารงานสมรสและครอบครัวไทย–ต่างชาติ 6 กรณี ตั้งแต่หนังสือรับรองความเป็นโสด ทะเบียนสมรสใช้ต่างประเทศ บันทึกฐานะแห่งครอบครัว (คร.22) สัญญาก่อนสมรส วีซ่าคู่สมรส และการหย่า/เปลี่ยนนามสกุล";

export const Route = createFileRoute("/marriage/documents/")({
  loader: async () => {
    const { MARRIAGE_DOC_GROUPS, MARRIAGE_APOSTILLE_TH, MARRIAGE_EQUALITY_TH } = await import("@/data/marriage-doc-groups");
    return {
      __d: { MARRIAGE_DOC_GROUPS, MARRIAGE_APOSTILLE_TH, MARRIAGE_EQUALITY_TH },
      groups: MARRIAGE_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: EN_URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กลุ่มเอกสารงานสมรสและครอบครัวไทย–ต่างชาติ",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: MarriageDocumentsPage,
});

function MarriageDocumentsPage() {
  const { __d: { MARRIAGE_DOC_GROUPS, MARRIAGE_APOSTILLE_TH, MARRIAGE_EQUALITY_TH } } = Route.useLoaderData() as { __d: typeof import("@/data/marriage-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/marriage" className="hover:underline">
          จดทะเบียนสมรสไทย–ต่างชาติ
        </Link>{" "}
        / เอกสารตามกรณี
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">เอกสารงานสมรสและครอบครัว — 6 กลุ่มกรณี</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        การสมรสตามกฎหมายไทยมีผลเมื่อจดทะเบียนตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1457
        เอกสารที่ต้องใช้จึงต่างกันตามว่าคู่สมรสจดทะเบียนในไทยหรือต่างประเทศ และจะนำเอกสารไปใช้กับหน่วยงานใด
        หน้านี้จัดกลุ่มไว้ 6 กรณี พร้อมรายการเอกสาร ลำดับนิติกรณ์ และเช็กลิสต์ก่อนยื่น
        เงื่อนไขบางอย่างผันแปรตามสำนักทะเบียนและประเทศปลายทาง โปรดตรวจสอบกับหน่วยงานต้นทางก่อนเดินเรื่องทุกครั้ง
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        {MARRIAGE_EQUALITY_TH}
      </aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        {MARRIAGE_APOSTILLE_TH}
      </aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {MARRIAGE_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/marriage/documents/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {MARRIAGE_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/marriage/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านคู่มือ {g.titleTh} ฉบับเต็ม →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        ส่งสำเนาเอกสารให้ทีมงานตรวจก่อนยื่นได้ทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd
        เจ้าหน้าที่จะแจ้งขั้นตอน เอกสารที่ยังขาด และกรอบเวลาที่ทำได้จริงตามกรณีของคุณ
      </p>
          <ServiceAuthorityBlock topic="marriage" locale="th" />
    </main>
  );
}
